'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { findDOMNode } from 'react-dom';
import { KEYS } from 'DesignData/enums/Keys';
import { createOptimizedOmit } from 'DesignData/lib/object';
import { getKeybindingHandler } from 'DesignComponents/utils/keys';
const omitPropsForRender = createOptimizedOmit(['keybindingConfig', 'trackInteraction']); // TODO: Temp hack. Move elsewhere.

const isInputElement = el => {
  if (el == null) {
    return false;
  }

  if (el.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }

  if (el.isContentEditable) {
    return true;
  }

  if (el.nodeName === 'INPUT' || el.nodeName === 'TEXTAREA' || el.nodeName === 'SELECT' || el.nodeName === 'OPTION' || el.nodeName === 'BUTTON') {
    return true;
  }

  return false;
};

const STYLE_NO_OUTLINE = {
  outline: 'none'
};
const mapStateToProps = null;
/**
 * Largely borrows from https://git.hubteam.com/HubSpot/OffersUI/blob/master/static/js/redux/keyboardShortcuts.js
 * Example of keybindingConfig
 *
 * {
 *   [MODIFIERS.COMMAND]: {
 *     [KEYS.S]: saveCallback,
 *     [KEYS.Z]: undoCallback
 *   },
 *   [MODIFIERS.SHIFT]: {
 *     [KEYS.W]: shiftWCallback
 *   },
 *   [MODIFIERS.COMMAND_SHIFT]: {
 *     [KEYS.Z]: redoCallback
 *   },
 *   [MODIFIERS.NONE]: {
 *     [KEYS.BACKSPACE]: backspaceCallback,
 *     [KEYS.DELETE]: deleteCallback
 *   }
 * }
 */

/**
 * High Order Component adding a keyboard shortcuts to the component based on a
 * keybindingConfig prop that maps keybindings to callbacks
 */

export default function withKeyboardShortcuts() {
  return WrappedComponent => {
    class HighOrderKeyboardShortcuts extends Component {
      constructor(...args) {
        super(...args);

        this.keydownHandler = e => {
          const {
            keybindingConfig
          } = this.props;
          const handler = getKeybindingHandler(keybindingConfig, e);

          if (handler && !this.isWhitelistedForInputElements(e)) {
            e.preventDefault();
            e.stopPropagation();
            handler(e);
            this.refocus();
          }
        };

        this.refocus = () => {
          if (!this._mounted) {
            return;
          }

          const el = this.getDomNode();

          if (!el || el === document.activeElement || el.contains(document.activeElement)) {
            return;
          }

          el.focus();
        };
      }

      componentDidMount() {
        this._mounted = true;
        this.getDomNode().addEventListener('keydown', this.keydownHandler);
      }

      componentWillUnmount() {
        this._mounted = false;
        this.getDomNode().removeEventListener('keydown', this.keydownHandler);
      }

      getDomNode() {
        if (this.props.keybindingConfig.listenOnDocument) {
          return document;
        }

        return findDOMNode(this);
      } // TODO: Temp hack. We need to rethink key configs in general a bit.


      isWhitelistedForInputElements(e) {
        if (!(isInputElement(e.target) || isInputElement(e.currentTarget))) {
          return false;
        } // Filter out usages of DELETE/BACKSPACE/FORWARD_SLASH from input elements.


        if (e.keyCode === KEYS.BACKSPACE || e.code === 0x000e || e.code === 'Backspace' || e.keyCode === KEYS.DELETE || e.code === 0xe053 || e.code === 'Delete' || e.keyCode === KEYS.FORWARD_SLASH) {
          return true;
        }

        return false;
      }

      render() {
        return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({
          style: STYLE_NO_OUTLINE,
          tabIndex: 0
        }, omitPropsForRender(this.props)));
      }

    }

    HighOrderKeyboardShortcuts.propTypes = {
      keybindingConfig: PropTypes.object
    };
    HighOrderKeyboardShortcuts.defaultProps = {
      keybindingConfig: {}
    };
    return connect(mapStateToProps)(HighOrderKeyboardShortcuts);
  };
}